import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import type { Catalog, PerksState } from '@/lib/perks'

const PERKS_KEY = ['perks']

export function usePerks() {
  return useQuery({
    queryKey: PERKS_KEY,
    queryFn: async () => (await api.get<PerksState>('/perks')).data,
    staleTime: 60_000,
  })
}

export function usePerksCatalog() {
  return useQuery({
    queryKey: ['perks-catalog'],
    queryFn: async () => (await api.get<Catalog>('/perks/catalog')).data,
    staleTime: Infinity,
  })
}

/** Runs a perks mutation; every endpoint answers with the full state, which replaces the cache. */
export function usePerksMutation() {
  const queryClient = useQueryClient()
  return useCallback(
    async (call: () => Promise<{ data: PerksState }>) => {
      const response = await call()
      queryClient.setQueryData(PERKS_KEY, response.data)
      return response.data
    },
    [queryClient]
  )
}

type Fields = Record<string, unknown>

/** The API's `{error}` message, or a generic one. */
export function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string } } })?.response?.data
  return data?.error ?? 'Something went wrong. Please try again.'
}

export const perksApi = {
  createCard: (body: Fields) => api.post<PerksState>('/perks/cards', body),
  updateCard: (id: number, fields: Fields) => api.patch<PerksState>(`/perks/cards/${id}`, fields),
  deleteCard: (id: number) => api.delete<PerksState>(`/perks/cards/${id}`),
  createBenefit: (cardId: number, fields: Fields) => api.post<PerksState>(`/perks/cards/${cardId}/benefits`, fields),
  updateBenefit: (id: number, fields: Fields) => api.patch<PerksState>(`/perks/benefits/${id}`, fields),
  deleteBenefit: (id: number) => api.delete<PerksState>(`/perks/benefits/${id}`),
  logUse: (benefitId: number, body: { amount_cents: number; date: string; is_skip?: boolean; note?: string }) =>
    api.post<PerksState>(`/perks/benefits/${benefitId}/usages`, body),
  /** Assigns a statement credit to a benefit; future credits like it then match on their own. */
  assignCredit: (transactionId: string, benefitId: number) =>
    api.post<PerksState>(`/perks/benefits/${benefitId}/usages`, { transaction_id: transactionId }),
  deleteUsage: (id: number) => api.delete<PerksState>(`/perks/usages/${id}`),
  ignoreCredit: (transactionId: string) =>
    api.post<PerksState>(`/perks/transactions/${encodeURIComponent(transactionId)}/ignore`),
}
